// @ts-nocheck

import { useEffect } from 'react';
import { getSessionId } from '../../utils/get-or-create-session';
import { safeTrack } from '../../utils/analytics';
import { addToCartTracking } from '../../utils/cart-tracking';
import {
  getCurrentTrackingSource,
  shouldTrack,
  hasDirectTrackingFlag,
  getRecommendationMetadata,
  clearRecommendationMetadata,
} from '../../utils/tracking-session';

const ProductView: React.FC<{
  customer: string;
  store: string;
  product: string;
  provider: string;
}> = ({ customer, store, product, provider }) => {
  const storeType = provider;

  // Extract product ID from URL
  const getProductIdFromUrl = (url: string) => {
    const match = url.match(/\/p(\d+)$/);
    return match ? match[1] : null;
  };

  // Check if previous page was search
  const isFromSearchPage = () => {
    const referrer = document.referrer;
    return provider === 'salla'
      ? referrer.includes('/search?q=')
      : provider === 'zid'
      ? referrer.includes('/products?search=')
      : provider === 'woocommerce'
      ? referrer.includes('/search/')
      : false;
  };

  const getPreviousQuery = (provider: string): string => {
    // Check if document.referrer exists and is a valid URL
    if (!document.referrer || document.referrer.trim() === '') {
      return '';
    }

    try {
      const searchParams = new URL(document.referrer).searchParams;
      let query = '';
      switch (provider) {
        case 'zid': {
          query = searchParams.get('search') || '';
          break;
        }
        case 'salla': {
          query = searchParams.get('q') || '';
          break;
        }
        case 'woocommerce': {
          const normalQuery = searchParams.get('s') || '';
          const pathParts = window.location.pathname.split('/');
          const searchIndex = pathParts.indexOf('search');
          const permalinkQuery =
            searchIndex !== -1 && searchIndex + 1 < pathParts.length
              ? pathParts[searchIndex + 1]
              : '';
          query =
            normalQuery || permalinkQuery || searchParams.get('search') || '';
          break;
        }
        default: {
          query = '';
          break;
        }
      }
      return query;
    } catch (error) {
      // If URL constructor fails, log the error and return empty string
      return '';
    }
  };

  // Function to manage recently viewed products in localStorage
  const addToRecentlyViewed = (productId: string) => {
    const STORAGE_KEY = 'alfinder_recently_viewed';
    const MAX_ITEMS = 20; // Limit to prevent localStorage from growing too large

    try {
      // Get existing recently viewed products
      const existingItems = localStorage.getItem(STORAGE_KEY);
      let recentlyViewed: string[] = existingItems
        ? JSON.parse(existingItems)
        : [];

      // Check if product is already in the list
      if (!recentlyViewed.includes(productId)) {
        // Add new product to the beginning of the array
        recentlyViewed.unshift(productId);

        // Limit the array size
        if (recentlyViewed.length > MAX_ITEMS) {
          recentlyViewed = recentlyViewed.slice(0, MAX_ITEMS);
        }

        // Save back to localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(recentlyViewed));
      }
    } catch (error) {
      console.error('Error managing recently viewed products:', error);
    }
  };

  useEffect(() => {
    const sessionId = getSessionId();

    // Add product to recently viewed
    addToRecentlyViewed(product);

    safeTrack('product_viewed', {
      storeId: store,
      storeType,
      provider: provider,
      product,
      query: getPreviousQuery(provider),
      customer,
      sessionId,
    });

    let originalPush = window.alfinderDataLayer?.push;

    window.alfinderDataLayer.push = function (...args) {
      const result = originalPush.apply(this, args);
      console.log('args', args);

      const customEvent = args[0];
      if (
        customEvent?.event === 'cart::item.added' &&
        shouldTrack(product) &&
        !hasDirectTrackingFlag() // Skip if direct tracking was already done
      ) {
        const productDetails = customEvent;
        const productName = productDetails?.productName;
        const currentTrackingSource = getCurrentTrackingSource();

        // Only track if we have a valid product name and tracking source
        if (productName && currentTrackingSource) {
          console.log(
            'ProductView: Handling cart tracking since no direct tracking was done',
          );

          // Get recommendation metadata if source is recommendations
          const metadata =
            currentTrackingSource === 'recommendations'
              ? getRecommendationMetadata()
              : null;

          addToCartTracking(
            productName,
            currentTrackingSource,
            metadata || undefined,
          );

          safeTrack('product_added_to_cart', {
            storeId: store,
            storeType,
            provider: provider,
            product,
            productName,
            query: getPreviousQuery(provider),
            customer,
            type: 'product_details_page',
            reached_from: currentTrackingSource,
            ...(metadata && {
              pageType: metadata.pageType,
              recommendationType: metadata.recommendationType,
            }),
          });

          // Clear metadata after tracking to prevent reuse
          if (metadata) {
            clearRecommendationMetadata();
          }

          console.log('product_added_to_cart', {
            storeId: store,
            storeType,
            provider: provider,
            product,
            reached_from: currentTrackingSource,
            productName:
              provider === 'salla'
                ? productDetails?.name
                : productDetails?.productName,
            query: getPreviousQuery(provider),
            customer,
          });
        }
      } else if (
        customEvent?.event === 'cart::item.added' &&
        hasDirectTrackingFlag()
      ) {
        console.log(
          'ProductView: Skipping cart tracking - direct tracking was already done',
        );
      }

      return result;
    };

    // Store references to buttons with listeners
    const buttonsWithListeners = new Set();

    // Add listeners to checkout buttons - now with more comprehensive selectors
    const checkoutButtons =
      document.querySelectorAll(
        'button[class*="checkout"], a[class*="checkout"], button[class*="-checkout-"], button[class*="-checkout"], salla-mini-checkout-widget',
      ) || [];

    checkoutButtons.forEach((button) => {
      const clickHandler = (e) => {
        if (shouldTrack(product)) {
          const currentTrackingSource = getCurrentTrackingSource();

          // Only track if we have a valid source
          if (currentTrackingSource) {
            safeTrack('started_purchasing_product', {
              storeId: store,
              storeType,
              provider: provider,
              product,
              query: getPreviousQuery(provider),
              customer,
              type: 'product_details_page',
              reached_from: currentTrackingSource,
            });
          }
        }
      };

      button.addEventListener('click', clickHandler);
      buttonsWithListeners.add({ element: button, handler: clickHandler });
    });

    return () => {
      // Remove all event listeners
      if (buttonsWithListeners.size > 0) {
        buttonsWithListeners.forEach(({ element, handler }) => {
          element.removeEventListener('click', handler);
        });
      }
    };
  }, []);

  return <></>;
};

export default ProductView;
